import axios from "axios";
import { Dispatch } from "redux";
import { toast } from "@teamprevent/common/toasts";
import { loadAndTrack } from "redux-request-loading";
import { Payload } from "@neworbit/redux-helpers";

import { SUBMIT_QUESTIONNAIRE, submitQuestionnaireSuccess, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import {
    MyResultTableEntryModel,
    ManagerSraQuestionnaireResultModel,
    TeamSraQuestionnaireResultModel
} from "@teamprevent/common/stressRiskAssessment/models";

import { SraApi } from "./SraApi";
import * as ActionTypes from "./actionTypes";

export type LoadSraTasksActionSuccess = ({ type: typeof ActionTypes.LOAD_SRA_TASKS_SUCCESS }) & Payload<MyResultTableEntryModel[]>;
export type LoadSraResultsActionSuccess = ({ type: typeof ActionTypes.LOAD_SRA_RESULTS_SUCCESS }) & Payload<ManagerSraQuestionnaireResultModel>;
export type LoadTeamSraResultsActionSuccess = ({ type: typeof ActionTypes.LOAD_TEAM_SRA_RESULTS_SUCCESS }) & Payload<TeamSraQuestionnaireResultModel>;

export type SraAction = LoadSraTasksActionSuccess |
    LoadSraResultsActionSuccess |
    LoadTeamSraResultsActionSuccess;

export const submitQuestionnaire = buildSubmitQuestionnaire(new SraApi(axios));

export function buildSubmitQuestionnaire(sraApi: SraApi) {
    return (questions: AnsweredQuestionWithId[], questionnaireKey: string) => {
        if (!questions.every(question => question.isValid)) {
            return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey, true));
        }
        return async (dispatch: Dispatch) => {
            await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, sraApi.submit(questions));
            toast.success("SRA Case submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
        };
    };
}

export const resendCompletionRequest = buildResendCompletionRequest(new SraApi(axios));

export function buildResendCompletionRequest(sraApi: SraApi) {
    return (sraManagerSectionId: string) => {
        return (dispatch: Dispatch) =>
            loadAndTrack(
                dispatch,
                ActionTypes.RESEND_COMPLETION_REQUEST,
                sraApi.resendCompletionRequest(sraManagerSectionId));
    };
}

export const loadSraTasks = buildLoadSraTasks(new SraApi(axios));

export function buildLoadSraTasks(sraApi: SraApi) {
    return () => {
        return async (dispatch: Dispatch) => {
            const response = await loadAndTrack(dispatch, ActionTypes.LOAD_SRA_TASKS, sraApi.loadSraTasks());
            if (response) {
                dispatch(loadSraTasksSuccess(response));
            }
        };
    };
}

export const loadSraResults = buildLoadSraResults(new SraApi(axios));

export function buildLoadSraResults(sraApi: SraApi) {
    return (sraEmployeeSectionId: string) => {
        return async (dispatch: Dispatch) => {
            const response = await loadAndTrack(dispatch, ActionTypes.LOAD_SRA_RESULTS, sraApi.loadSraResults(sraEmployeeSectionId));
            if (response) {
                dispatch(loadSraResultsSuccess(response));
            }
        };
    };
}

export const loadSraResultsForCase = buildLoadSraResultsForCase(new SraApi(axios));

export function buildLoadSraResultsForCase(sraApi: SraApi) {
    return (caseId: string) => {
        return async (dispatch: Dispatch) => {
            const response = await loadAndTrack(dispatch, ActionTypes.LOAD_SRA_RESULTS, sraApi.loadSraResultsForCase(caseId));
            if (response) {
                dispatch(loadSraResultsSuccess(response));
            }
        };
    };
}

export const loadTeamSraResults = buildTeamLoadSraResults(new SraApi(axios));

export function buildTeamLoadSraResults(sraApi: SraApi) {
    return (departmentId: string) => {
        return async (dispatch: Dispatch) => {
            const response = await loadAndTrack(dispatch, ActionTypes.LOAD_TEAM_SRA_RESULTS, sraApi.loadTeamSraResults(departmentId));
            if (response) {
                dispatch(loadTeamSraResultsSuccess(response));
            }
        };
    };
}

export const loadSraResultsSuccess = (sraResult: ManagerSraQuestionnaireResultModel): LoadSraResultsActionSuccess => {
    return {
        type: ActionTypes.LOAD_SRA_RESULTS_SUCCESS,
        payload: sraResult
    };
};

export const loadTeamSraResultsSuccess = (teamSraResult: TeamSraQuestionnaireResultModel): LoadTeamSraResultsActionSuccess => {
    return {
        type: ActionTypes.LOAD_TEAM_SRA_RESULTS_SUCCESS,
        payload: teamSraResult
    };
};

export const loadSraTasksSuccess = (sraTasks: MyResultTableEntryModel[]): LoadSraTasksActionSuccess => {
    return {
        type: ActionTypes.LOAD_SRA_TASKS_SUCCESS,
        payload: sraTasks
    };
};
